import { useEffect, useMemo, useState } from 'react';
import {
  Send, Sparkles, Loader2, CheckCircle2, AlertTriangle,
  RefreshCw, Link2, ExternalLink,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogClose,
} from '@/components/common/Dialog';
import { Button } from '@/components/common/Button';
import { Select } from '@/components/common/Select';
import { Badge } from '@/components/common/Badge';
import { useToast } from '@/components/common/Toast';
import magnetService, { type Magnet } from '@/services/api/magnetService';
import emailService, { type MagnetPreview } from './emailService';

type Tone = 'warm' | 'direct' | 'formal' | 'concise';
type Phase = 'select' | 'previewing' | 'preview' | 'sending' | 'sent';

interface Props {
  open: boolean;
  onClose: () => void;
  contactId: string;
  contactName: string;
  contactEmail?: string | null;
  currentStage?: string | null;   // e.g. 'Stage1'
  journeyId?: string;
  initialMagnetId?: string;       // preselect a magnet (e.g. from a journey stage)
  onSent?: () => void;            // refresh the timeline after a successful send
}

const TONES: { value: Tone; label: string }[] = [
  { value: 'warm', label: 'Warm' },
  { value: 'direct', label: 'Direct' },
  { value: 'formal', label: 'Formal' },
  { value: 'concise', label: 'Concise' },
];

const stageNum = (s?: string | null) => {
  const m = (s || '').match(/\d+/);
  return m ? Math.min(14, Math.max(1, parseInt(m[0], 10))) : undefined;
};

export default function SendMagnetModal({
  open, onClose, contactId, contactName, contactEmail, currentStage, journeyId, initialMagnetId, onSent,
}: Props) {
  const { addToast } = useToast();

  const [magnets, setMagnets] = useState<Magnet[]>([]);
  const [loadingMagnets, setLoadingMagnets] = useState(false);
  const [magnetId, setMagnetId] = useState('');
  const [tone, setTone] = useState<Tone>('warm');

  const [phase, setPhase] = useState<Phase>('select');
  const [error, setError] = useState<string | null>(null);

  const [preview, setPreview] = useState<MagnetPreview | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sentInfo, setSentInfo] = useState<{ messageId: string; to: string } | null>(null);

  // Reset state each time the modal opens, then load the magnet list.
  useEffect(() => {
    if (!open) return;
    setPhase('select');
    setError(null);
    setPreview(null);
    setSubject('');
    setBody('');
    setSentInfo(null);
    setMagnetId(initialMagnetId || '');
    setTone('warm');

    setLoadingMagnets(true);
    magnetService.getAllMagnets(undefined, 1, 100)
      .then((res) => setMagnets(res.data || []))
      .catch(() => addToast({
        type: 'error', title: 'Could not load magnets', description: 'Try reopening the dialog.',
      }))
      .finally(() => setLoadingMagnets(false));
  }, [open]);

  const magnetOptions = useMemo(
    () => magnets.map((m) => ({ value: m.id, label: m.title })),
    [magnets],
  );

  const runPreview = async () => {
    if (!magnetId) return;
    setPhase('previewing');
    setError(null);
    try {
      const p = await emailService.previewMagnet({
        contactId, magnetId, journeyId, stage: stageNum(currentStage), tone,
      });
      setPreview(p);
      setSubject(p.subject);
      setBody(p.body);
      setPhase('preview');
    } catch (e: any) {
      setError(e?.response?.data?.details || e?.response?.data?.error || (e?.response ? 'Failed to generate preview' : 'Preview timed out or the server was unreachable — try again'));
      setPhase('select');
    }
  };

  const runSend = async () => {
    if (!preview) return;
    setPhase('sending');
    setError(null);
    try {
      const r = await emailService.sendMagnet({
        contactId, magnetId, journeyId, stage: stageNum(currentStage), tone, subject, body,
      });
      setSentInfo({ messageId: r.messageId, to: r.to });
      setPhase('sent');
      addToast({ type: 'success', title: 'Email sent', description: `Magnet delivered to ${r.to}` });
      onSent?.();
    } catch (e: any) {
      setError(e?.response?.data?.details || e?.response?.data?.error || 'Send failed');
      setPhase('preview');
    }
  };

  const inPreview = phase === 'preview' || phase === 'sending';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogClose onClose={onClose} />
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Send Magnet
          </DialogTitle>
          <DialogDescription>
            AI-personalised resource email to {contactName}
            {contactEmail ? ` \u00b7 ${contactEmail}` : ''}
          </DialogDescription>
        </DialogHeader>

        {!contactEmail && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            This contact has no email address. Add one before sending.
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        {phase === 'sent' && sentInfo ? (
          <div className="py-6 text-center space-y-3">
            <CheckCircle2 className="h-12 w-12 mx-auto text-green-600" />
            <div>
              <p className="font-medium">Sent to {sentInfo.to}</p>
              <p className="text-xs text-muted-foreground mt-1 break-all">
                SES message {sentInfo.messageId}
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              Opens and clicks will now appear on the contact timeline.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Resource</label>
                <Select
                  options={magnetOptions}
                  value={magnetId}
                  onChange={(v) => { setMagnetId(v); setPhase('select'); setPreview(null); }}
                  placeholder={loadingMagnets ? 'Loading\u2026' : 'Choose a magnet'}
                  disabled={loadingMagnets || phase === 'sending'}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Tone</label>
                <Select
                  options={TONES}
                  value={tone}
                  onChange={(v) => setTone(v as Tone)}
                  disabled={phase === 'sending'}
                />
              </div>
            </div>

            {preview && (
              <div className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
                preview.ses.fromConfigured
                  ? 'border-blue-200 bg-blue-50 text-blue-800'
                  : 'border-amber-300 bg-amber-50 text-amber-800'
              }`}>
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                {preview.ses.fromConfigured ? (
                  <span>
                    Sending via {{
                      ses: 'Amazon SES',
                      smtp: 'SMTP',
                      sendgrid: 'SendGrid',
                      resend: 'Resend',
                    }[preview.ses.provider as string] || (preview.ses.provider || 'the configured provider')}
                    {preview.ses.provider === 'ses' && preview.ses.region ? ` (${preview.ses.region})` : ''}.
                    {preview.ses.provider === 'ses'
                      ? ' While SES is in sandbox, the recipient must be a verified identity.'
                      : ''}
                  </span>
                ) : (
                  <span>Email sender not configured (from address missing) &mdash; sending is disabled. Set it in Settings &rarr; Integrations &rarr; Email.</span>
                )}
              </div>
            )}

            {inPreview && preview && (
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Subject</label>
                  <input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    disabled={phase === 'sending'}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Body</label>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    disabled={phase === 'sending'}
                    rows={10}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                  />
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Link2 className="h-3 w-3 shrink-0" />
                    <span>
                      <code>{'{{MAGNET_LINK}}'}</code> becomes a tracked link to
                      {' \u201c'}{preview.magnet.title}{'\u201d'}.
                    </span>
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <a
                    href={preview.magnet.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline break-all"
                  >
                    <ExternalLink className="h-3 w-3 shrink-0" /> {preview.magnet.link}
                  </a>
                  {preview.model && <Badge variant="default">{preview.model}</Badge>}
                </div>
              </div>
            )}
          </div>
        )}

        {phase === 'sent' ? (
          <DialogFooter>
            <Button onClick={onClose}>Done</Button>
          </DialogFooter>
        ) : (
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={phase === 'sending'}>
              Cancel
            </Button>

            {inPreview ? (
              <>
                <Button variant="outline" onClick={runPreview} disabled={phase === 'sending'}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Regenerate
                </Button>
                <Button
                  onClick={runSend}
                  disabled={
                    !contactEmail ||
                    !preview?.ses.fromConfigured ||
                    !subject.trim() ||
                    !body.trim() ||
                    phase === 'sending'
                  }
                >
                  {phase === 'sending'
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending&hellip;</>
                    : <><Send className="h-4 w-4 mr-2" /> Send email</>}
                </Button>
              </>
            ) : (
              <Button
                onClick={runPreview}
                disabled={!magnetId || !contactEmail || phase === 'previewing'}
              >
                {phase === 'previewing'
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating&hellip;</>
                  : <><Sparkles className="h-4 w-4 mr-2" /> Generate preview</>}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
